import {GlobalSearchVisibilityMode} from './GlobalSearchVisibilityMode';
export type {GlobalSearchVisibilityMode} from './GlobalSearchVisibilityMode';
import {GlobalSearchPathSegment} from './GlobalSearchPathSegment';
export type {GlobalSearchPathSegment} from './GlobalSearchPathSegment';
import {GlobalSearchContainerResultDto} from './GlobalSearchContainerResultDto';
export type {GlobalSearchContainerResultDto} from './GlobalSearchContainerResultDto';
import {GlobalSearchItemResultDto} from './GlobalSearchItemResultDto';
export type {GlobalSearchItemResultDto} from './GlobalSearchItemResultDto';
import {GlobalSearchPathSegmentDto} from './GlobalSearchPathSegmentDto';
export type {GlobalSearchPathSegmentDto} from './GlobalSearchPathSegmentDto';
import {GlobalSearchResponseDto} from './GlobalSearchResponseDto';
export type {GlobalSearchResponseDto} from './GlobalSearchResponseDto';
import {GlobalSearchContainerResult} from './GlobalSearchContainerResult';
export type {GlobalSearchContainerResult} from './GlobalSearchContainerResult';
import {GlobalSearchItemResult} from './GlobalSearchItemResult';
export type {GlobalSearchItemResult} from './GlobalSearchItemResult';
import {GlobalSearchResults} from './GlobalSearchResults';
export type {GlobalSearchResults} from './GlobalSearchResults';
import {mapStockFacts} from '../components/sm-stock-summary/stock-summary.model';
import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {catchError, forkJoin, map, Observable, of, tap} from 'rxjs';
import {ContainersService, type ContainerTreeItem} from '../../containers/containers.service';
import {ApiResponse} from '../../auth/auth.models';
import {buildApiUrl} from '../api/api-url';
import {AnalyticsService} from '../analytics/analytics.service';
import {SearchIntent} from '../scanning/code-scan';

export const DEFAULT_GLOBAL_SEARCH_VISIBILITY_MODE: GlobalSearchVisibilityMode = 'withoutZeroCount';

function toSearchPathSegment(dto: GlobalSearchPathSegmentDto): GlobalSearchPathSegment {
  return {
    id: dto.id,
    name: dto.name,
  };
}

export function toSearchContainerResult(dto: GlobalSearchContainerResultDto): GlobalSearchContainerResult {
  return {
    description: dto.description,
    hasAssignedQrCode: dto.hasAssignedQrCode,
    id: dto.id,
    imageUrl: dto.imageUrl,
    name: dto.name,
    path: (dto.path ?? []).map(toSearchPathSegment),
  };
}

export function toSearchItemResult(dto: GlobalSearchItemResultDto): GlobalSearchItemResult {
  return {
    containerId: dto.locationId,
    containerName: dto.locationName,
    containerPath: (dto.locationPath ?? []).map(toSearchPathSegment),
    description: dto.description,
    hasAssignedQrCode: dto.hasAssignedQrCode,
    id: dto.id,
    imageUrl: dto.imageUrl,
    name: dto.name,
    locationId: dto.locationId,
    ...mapStockFacts(dto),
    quantity: dto.quantity,
    unitLabel: dto.unitLabel ?? null,
  };
}

function buildContainerPath(
  containerId: string,
  containersById: Map<string, ContainerTreeItem>,
): GlobalSearchPathSegment[] {
  const path: GlobalSearchPathSegment[] = [];
  const visited = new Set<string>();
  let currentId: string | null = containerId;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);

    const current = containersById.get(currentId);
    if (!current) {
      break;
    }

    path.push({
      id: current.id,
      name: current.name,
    });

    if (current.parentId === null || current.isSharedRoot) {
      break;
    }

    currentId = current.parentId;
  }

  return path.reverse();
}

@Injectable({
  providedIn: 'root',
})
export class GlobalSearchService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly containersService: ContainersService,
    private readonly analyticsService: AnalyticsService,
  ) {}

  search(
    query: string,
    visibilityMode: GlobalSearchVisibilityMode = DEFAULT_GLOBAL_SEARCH_VISIBILITY_MODE,
    intent: SearchIntent = 'text',
  ): Observable<GlobalSearchResults> {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      return of({
        containers: [],
        hasMoreContainers: false,
        hasMoreItems: false,
        items: [],
      });
    }

    const searchParams = new URLSearchParams({
      query: normalizedQuery,
      visibility: visibilityMode,
    });
    if (intent !== 'text') searchParams.set('intent', intent);

    const searchRequest$ = this.httpClient.get<ApiResponse<GlobalSearchResponseDto>>(
      buildApiUrl(`/v1/item-containers/search?${searchParams.toString()}`),
    );

    const containerTree$ = this.containersService.getTree().pipe(
      catchError(() => of([])),
    );

    return forkJoin({
      payload: searchRequest$,
      containerTree: containerTree$,
    }).pipe(
      map(({payload, containerTree}) => {
        if (!payload.data) {
          throw new Error('Global search response is missing.');
        }

        const containersById = new Map(containerTree.map((item) => [item.id, item]));

        return {
          containers: (payload.data.locations ?? []).map(toSearchContainerResult),
          hasMoreContainers: payload.data.hasMoreLocations === true,
          hasMoreItems: payload.data.hasMoreItems === true,
          items: (payload.data.items ?? []).map((item) => {
            const result = toSearchItemResult(item);

            if (result.containerPath.length > 0) {
              return result;
            }

            const resolvedPath = buildContainerPath(result.locationId || result.containerId, containersById);
            return {
              ...result,
              containerPath: resolvedPath,
            };
          }),
        };
      }),
      tap(() => void this.analyticsService.searchCompleted('global')),
    );
  }
}
